import { useAppStore } from "@hooks/useAppStore";
import { isAndroid, isWeb } from "@memorang/helpers/platform";
import Box from "@memorang/ui/components/Box";
import type { FeatureProp } from "@memorang/ui/components/WelcomeFeaturesList";
import FeatureList from "@memorang/ui/components/WelcomeFeaturesList";
import useBreakpoints from "@memorang/ui/useBreakpoints";
import { useNormalizeFontSize } from "@memorang/ui/useNormalizeFontSize";
import { Text as NativeText } from "react-native";
import { Text, useTheme } from "react-native-paper";
import ConsentContainer from "../components/ConsentContainer";

type Props = {
	handleNext: () => void;
	subtitle?: string;
	valueProps?: FeatureProp[];
	title?: {
		prefix?: string;
		decoratedText?: string;
		suffix?: string;
	};
	marketText?: {
		prefix?: string;
		suffix?: string;
	};
};

const Landing = ({
	handleNext,
	valueProps,
	subtitle,
	title,
	marketText,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: Props) => {
	const { normalizeFontSize } = useNormalizeFontSize();
	const theme = useTheme();
	const {
		smUp: isTabletUp,
		lgUp: isDesktopLargeUp,
		smDown: isMobileDown,
		mdUp: isDesktopUp,
	} = useBreakpoints();
	const appFont = useAppStore((state) => state.app.font) || "opensans";

	const shouldCenterContent = isWeb && isDesktopLargeUp;
	const buttonPosition = isTabletUp && isWeb ? "relative" : "absolute";
	const maxTitleWidth =
		isTabletUp && isWeb ? (isDesktopUp ? 440 : 360) : isMobileDown ? 275 : 360;
	const gapSize = isMobileDown ? 48 : 36;

	return (
		<Box
			paddingTop={isDesktopLargeUp ? 24 : 36}
			flexGrow={1}
			position="relative"
			justifyContent={shouldCenterContent ? "center" : "flex-start"}
			flex={1}
			width="100%"
		>
			<Box
				alignItems="center"
				gap={36}
				flexGrow={1}
				justifyContent={shouldCenterContent ? "center" : "flex-start"}
			>
				{subtitle && (
					<Text
						style={{
							fontStyle: "italic",
							fontFamily: `${appFont}-italic`,
						}}
						variant="headlineSmall"
					>
						{subtitle}
					</Text>
				)}

				<Box gap={gapSize} justifyContent="center">
					<Box
						gap={12}
						justifyContent="center"
						alignItems="center"
						alignSelf="center"
						maxWidth={maxTitleWidth}
					>
						{title && (
							<NativeText
								style={{
									lineHeight: normalizeFontSize(34),
									textAlign: "center",
									color: theme.colors.onSurfaceVariant,
									fontFamily: `${appFont}-semibold`,
									fontSize: normalizeFontSize(28),
								}}
								numberOfLines={3}
								adjustsFontSizeToFit
							>
								{`${title.prefix} `}
								<NativeText
									style={{
										textDecorationLine: "underline",
										fontFamily: `${appFont}-semibold`,
									}}
								>
									{`${title.decoratedText}`}
								</NativeText>
								{` ${title.suffix}`}
							</NativeText>
						)}

						{marketText && (
							<Text
								variant="bodyMedium"
								style={{
									textAlign: "center",
									alignSelf: "center",
									color: theme.colors.onSurfaceVariant,
								}}
							>
								<Text
									variant="titleSmall"
									style={{ fontWeight: "900", color: theme.colors.secondary }}
								>
									{marketText.prefix}
								</Text>{" "}
								{marketText.suffix}
							</Text>
						)}
					</Box>

					{!isTabletUp && isWeb && valueProps && (
						<FeatureList valueProps={valueProps} />
					)}
				</Box>

				<Box
					alignItems="center"
					gap={16}
					position={buttonPosition}
					bottom={isTabletUp && isWeb ? undefined : isAndroid ? 0 : 16}
					style={{
						maxWidth: isTabletUp && isWeb ? (isDesktopUp ? 440 : 360) : "100%",
					}}
					left={isTabletUp && isWeb ? undefined : 0}
					width="100%"
				>
					<Box
						style={{
							width: "100%",
							maxWidth:
								!isDesktopLargeUp && !isWeb
									? isDesktopUp
										? 440
										: 360
									: "100%",
							alignItems: "center",
						}}
					>
						<ConsentContainer onContinue={handleNext} />
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default Landing;
